import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useCarrierForms,
  useCarrierSummaries,
  useDeleteForm,
  type FormListItem,
} from '@/features/quote-and-apply-builder';

export function useCarrierFormsList() {
  const { carrierId } = useParams<{ carrierId: string }>();
  const navigate = useNavigate();
  const carrierIdNum = Number(carrierId);

  const { data: carriers } = useCarrierSummaries();
  const carrier = carriers?.find(carrier => carrier.insuranceCarrierId === carrierIdNum);

  const { data: forms, isLoading, error } = useCarrierForms(carrierIdNum);
  const { mutate: deleteForm, isPending: isDeleting } = useDeleteForm();
  const [formToDelete, setFormToDelete] = useState<FormListItem | null>(null);

  const handleDeleteClick = useCallback((event: React.MouseEvent, form: FormListItem) => {
    event.stopPropagation();
    setFormToDelete(form);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!formToDelete) return;

    deleteForm(formToDelete.id, {
      onSuccess: () => {
        toast.success(`"${formToDelete.displayName}" has been deleted`);
        setFormToDelete(null);
      },
      onError: err => {
        toast.error(err instanceof Error ? err.message : 'Failed to delete form');
        setFormToDelete(null);
      },
    });
  }, [formToDelete, deleteForm]);

  const handleDismissDelete = useCallback((open: boolean) => {
    if (!open) setFormToDelete(null);
  }, []);

  const navigateBack = useCallback(() => navigate('/wizard/form-builder'), [navigate]);
  const navigateToNewForm = useCallback(() => navigate('/wizard/form-builder/new'), [navigate]);
  const navigateToForm = useCallback(
    (formId: string) => navigate(`/wizard/form-builder/${carrierId}/${formId}`),
    [navigate, carrierId],
  );
  const navigateToCompositeTemplates = useCallback(
    (formId: string) => navigate(`/wizard/form-builder/${carrierId}/${formId}/composite-templates`),
    [navigate, carrierId],
  );

  const isEmptyState = !isLoading && !error && !forms?.length;
  const isDataLoaded = !isLoading && !error && forms && forms.length > 0;

  return {
    carrier,
    carrierId,
    forms,
    isLoading,
    error,
    isDeleting,
    formToDelete,
    isEmptyState,
    isDataLoaded,
    handleDeleteClick,
    handleConfirmDelete,
    handleDismissDelete,
    navigateBack,
    navigateToNewForm,
    navigateToForm,
    navigateToCompositeTemplates,
  };
}
